import { ColDef, RowClickedEvent, themeAlpine } from 'ag-grid-community';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import Quill from 'quill';
import Delta from 'quill-delta';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useTheme } from 'styled-components';

import { useMarkdownConverter } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, Heading3, Paragraph, QuillInput, Skeleton } from '@treams/treams-ui';

import { autoSizeStrategy, defaultColDef } from './constants';
import { GridRef } from './types';

function WorkspaceCellRenderer(props: CustomCellRendererProps) {
	const [selected, setSelected] = useState(props.node.isSelected() ?? false);

	useEffect(() => {
		const listener = () => setSelected(props.node.isSelected() ?? false);
		props.node.addEventListener('rowSelected', listener);
		return () => {
			props.node.removeEventListener('rowSelected', listener);
		};
	}, [props.node]);

	return (
		<Flex alignItems="center" style={{ gap: 8 }}>
			<input type="radio" checked={selected} readOnly />
			{props.value}
		</Flex>
	);
}

const columnDefs: ColDef[] = [
	{
		field: 'alias',
		headerName: 'Workspaces',
		floatingFilter: true,
		suppressFloatingFilterButton: true,
		suppressHeaderMenuButton: true,
		cellRenderer: WorkspaceCellRenderer,
		resizable: false,
	},
];

export default function WorkspaceInstructions() {
	const { t } = useTranslation();
	const theme = useTheme();
	const gridRef: GridRef = useRef(null);
	const gridTheme = themeAlpine.withParams({
		accentColor: theme.colors.primary,
		rowHoverColor: theme.colors.lightGray,
	});

	const workspaceInstructionsRef = useRef<Quill | undefined>();
	const [setPromptInstruction] = graphql.useSetPromptInstructionMutation();
	const { markdownToQuillDelta, htmlToMarkdown } = useMarkdownConverter();
	const [workspaceInstructions, setWorkspaceInstructions] = useState<Delta | undefined>();

	const [selectedWorkspace, setSelectedWorkspace] = useState<{
		id: string;
		alias: string;
	} | null>(null);

	const workspacePromptInstructionQuery = graphql.usePromptInstructionQuery({
		skip: !selectedWorkspace,
		variables: {
			workspaceID: selectedWorkspace?.id ?? '',
			promptInstructionID: graphql.Prompt.All,
		},
	});

	const workspacesQuery = graphql.useWorkspacesMinimalQuery({
		variables: {
			filters: { isArchived: false },
			first: 1000,
		},
	});

	const rowData = useMemo(
		() =>
			workspacesQuery.data?.workspacesV2.edges
				.map(({ node }) => ({
					id: node.id,
					alias: node.alias,
				}))
				?.filter((workspace) => workspace.alias !== 'launch') ?? [],
		[workspacesQuery.data],
	);

	const saveInstructions = useCallback(
		async (ref: MutableRefObject<Quill | undefined>, workspaceID: string) => {
			if (!ref.current) return;

			const html = ref.current.getSemanticHTML();
			const instructions = htmlToMarkdown(html);

			await setPromptInstruction({
				variables: {
					input: {
						workspaceID,
						promptInstructionID: graphql.Prompt.All,
						instructions,
					},
				},
			});
		},
		[setPromptInstruction, htmlToMarkdown],
	);

	const onRowClicked = useCallback((event: RowClickedEvent) => {
		const workspace = event.data ?? null;

		setSelectedWorkspace(workspace);
	}, []);

	const onFirstDataRendered = useCallback(() => {
		if (!selectedWorkspace || !gridRef.current) return;

		gridRef.current.api.forEachNode((node) => {
			if (node.data?.id === selectedWorkspace.id) {
				node.setSelected(true);
			}
		});
	}, [selectedWorkspace]);

	useEffect(() => {
		const instruction = workspacePromptInstructionQuery.data?.promptInstruction;

		if (!instruction) {
			const empty = new Delta();
			setWorkspaceInstructions(empty);
			workspaceInstructionsRef.current?.setContents(empty, 'api');
			return;
		}

		const delta = markdownToQuillDelta(instruction.instructions);
		setWorkspaceInstructions(delta);
		workspaceInstructionsRef.current?.setContents(delta, 'api');
	}, [workspacePromptInstructionQuery.data?.promptInstruction, markdownToQuillDelta]);

	return (
		<>
			<Heading3 mb={1}>
				{t('settings.ai-workbench.workspace-instructions')}
				{selectedWorkspace && (
					<span style={{ color: theme.colors.primary }}>: {selectedWorkspace.alias}</span>
				)}
			</Heading3>
			<Paragraph mb={3}>{t('settings.ai-workbench.custom-instructions')}</Paragraph>
			<Flex width="100%">
				{selectedWorkspace && workspaceInstructions ? (
					<Box flex={1}>
						<QuillInput
							onQuill={(quillEditor) => {
								workspaceInstructionsRef.current = quillEditor;
							}}
							minHeight={300}
							value={workspaceInstructions}
							onChange={setWorkspaceInstructions}
							hasHeading
							hideLink
							placeholder={t('settings.ai-workbench.enter-instructions')}
						/>
					</Box>
				) : (
					<Box flex={1} display="flex" minHeight={400}>
						<Skeleton.Empty
							width="100%"
							contents={<Paragraph>{t('settings.ai-workbench.no-workspace-selected')}</Paragraph>}
						/>
					</Box>
				)}
				<Box width={250} flexShrink={0} pl={2}>
					<AgGridReact
						theme={gridTheme}
						ref={gridRef}
						rowData={rowData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						autoSizeStrategy={autoSizeStrategy}
						enableCellTextSelection
						suppressCellFocus
						suppressHorizontalScroll
						rowSelection={{ mode: 'singleRow', enableClickSelection: true, checkboxes: false }}
						onRowClicked={onRowClicked}
						onFirstDataRendered={onFirstDataRendered}
						rowStyle={{ cursor: 'pointer' }}
					/>
				</Box>
			</Flex>
			<Button
				title={t('common.save')}
				disabled={!selectedWorkspace}
				mt={2}
				onClick={() => {
					if (!selectedWorkspace) return;
					return saveInstructions(workspaceInstructionsRef, selectedWorkspace.id);
				}}
			/>
		</>
	);
}
